import { VoteType } from './index.js';
/**
 * Derives the pass/fail outcome of a motion from its recorded votes.
 *
 * Authoritative for both server-side persistence (`replaceMotionVotes`
 * derives the motion's `resultLegislativeStatusId` from this value) and
 * client-side rendering (the vote card falls back to this when the
 * motion's resolved status hasn't been hydrated yet, e.g. when an
 * action lacks a pass/fail status mapping).
 *
 * Uses parliamentary "present and voting" semantics — only `aye` and
 * `nay` affect the result. Abstain, recuse, and absent are treated as
 * non-votes. Returns `null` when there are no aye/nay votes (e.g. an
 * unanimous abstain — rare but well-defined). Ties resolve via
 * `tiebreakRule`, defaulting to `fail` when no rule is configured (the
 * safer of the two for parliamentary procedure).
 */
export const computeVoteOutcome = (votes, tiebreakRule) => {
    let ayeCount = 0;
    let nayCount = 0;
    for (const { vote } of votes) {
        if (vote === VoteType.aye) {
            ayeCount += 1;
        }
        else if (vote === VoteType.nay) {
            nayCount += 1;
        }
    }
    if (ayeCount === 0 && nayCount === 0) {
        return null;
    }
    if (ayeCount > nayCount) {
        return 'pass';
    }
    if (nayCount > ayeCount) {
        return 'fail';
    }
    return tiebreakRule === 'pass' ? 'pass' : 'fail';
};
/**
 * Maps a computed {@link VoteOutcome} to the `resultLegislativeStatusId` the
 * action assigns. Returns `null` for a `null` outcome or when the action has no
 * matching pass/fail status configured. Split from {@link computeMotionResultStatusId}
 * so callers that already hold the outcome (e.g. `replaceMotionVotes`, which also
 * needs the outcome for consent handling) don't recompute it.
 */
export const resultStatusIdForOutcome = (action, outcome) => {
    if (outcome === 'pass') {
        return action.passLegislativeStatusId ?? null;
    }
    if (outcome === 'fail') {
        return action.failLegislativeStatusId ?? null;
    }
    return null;
};
/**
 * Derives the `resultLegislativeStatusId` for a motion directly from its votes
 * and its action's pass/fail mapping + tiebreak rule. Convenience composition of
 * {@link computeVoteOutcome} + {@link resultStatusIdForOutcome} for callers that
 * only need the final status id (e.g. the Close Voting handler).
 */
export const computeMotionResultStatusId = (action, votes) => resultStatusIdForOutcome(action, computeVoteOutcome(votes, action.tiebreakRule));
/**
 * Decides how a motion's vote slot (`voteOrderById`) should change when its votes
 * are (re)recorded. The slot is what makes a vote row appear in the minutes/timeline
 * (see `item-records.ts`); it only matters for entry-scoped motions (privileged shell
 * motions have no timeline). Extracted so the bulk replace path, per-member casts, and
 * Open Voting all agree on when the slot appears or clears.
 *
 * - `insert-after-motion` — a tally now exists but no slot yet.
 * - `clear` — every vote was removed and a slot existed.
 * - `null` — no change needed (or a privileged motion with no entry).
 */
export const resolveVoteSlotChange = (params) => {
    const { minutesEntryId, currentVoteOrderById, voteCount } = params;
    if (minutesEntryId == null) {
        return null;
    }
    if (voteCount === 0 && currentVoteOrderById != null) {
        return 'clear';
    }
    if (voteCount > 0 && currentVoteOrderById == null) {
        return 'insert-after-motion';
    }
    return null;
};
/**
 * Whether a no-vote motion has been resolved to its action's pass status. Compared by id because the
 * editor/timeline motion records omit the result relation. This says "resolved", not "passed": a
 * pass status may be Tabled (no outcome) or Withdrawn (cancel), so callers show the status name.
 */
export const isResolvedWithoutVote = ({ legislativeAction, resultLegislativeStatusId, }) => !legislativeAction.isVoteRequired &&
    resultLegislativeStatusId !== null &&
    resultLegislativeStatusId === legislativeAction.passLegislativeStatus?.id;
